import { useCallback, useEffect, useState } from 'react';
import { Platform } from 'react-native';
import { logout } from '../../auth/application/authSession';
import { useAuthStore } from '../../auth/store/useAuthStore';
import { registerSessionResetHandler } from '../../shared/ports/sessionResetPort';
import { showInfo } from '../../shared/ui/appToast';
import {
  endActiveImpersonationSession,
  fetchActiveImpersonationContext,
  isImpersonationExpired,
} from './impersonationSession';
import { awaitSessionBootstrap } from '../session/hydrateSessionCachesFromBootstrap';
import { useImpersonationStore } from './useImpersonationStore';

registerSessionResetHandler(() => {
  useImpersonationStore.getState().clear();
});

function formatExpiresAt(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit', hour12: false });
}

/**
 * Carga el banner de impersonación (web) y cierra la sesión al expirar o al salir.
 */
export function useImpersonationLifecycle() {
  const userId = useAuthStore((s) => s.user?.id ?? null);
  const userEmail = useAuthStore((s) => s.user?.email ?? null);
  const context = useImpersonationStore((s) => s.context);
  const setContext = useImpersonationStore((s) => s.setContext);
  const setLoading = useImpersonationStore((s) => s.setLoading);
  const clear = useImpersonationStore((s) => s.clear);
  const [exiting, setExiting] = useState(false);

  useEffect(() => {
    if (Platform.OS !== 'web' || !userId) {
      clear();
      return;
    }
    // Magic link deja tokens en el hash; sacarlos de la barra/historial.
    if (typeof window !== 'undefined' && window.history?.replaceState) {
      const hash = window.location.hash ?? '';
      const q = window.location.search ?? '';
      if (hash.includes('access_token') || q.includes('impersonation=1')) {
        window.history.replaceState({}, '', `${window.location.origin}/`);
      }
    }
    let cancelled = false;
    setLoading(true);
    void (async () => {
      // El contexto viene embebido en bootstrap_mobile_session (sección
      // `impersonation`) — mismo promise del boot, sin RPC extra. Fallback a la
      // RPC suelta si el server no la embebe (rollout) o el bootstrap falló.
      const payload = await awaitSessionBootstrap(userId).catch(() => null);
      const next =
        payload?.impersonation !== undefined
          ? payload.impersonation
          : await fetchActiveImpersonationContext();
      if (cancelled) return;
      if (next && isImpersonationExpired(next.expiresAt)) {
        await endActiveImpersonationSession('expired');
        clear();
        await logout('impersonation_expired');
        showInfo('Impersonación finalizada', 'La sesión de soporte expiró.');
        return;
      }
      setContext(next);
    })();
    return () => {
      cancelled = true;
    };
  }, [userId, clear, setContext, setLoading]);

  useEffect(() => {
    if (!context) return;
    const ms = Date.parse(context.expiresAt) - Date.now();
    if (!Number.isFinite(ms)) return;
    const delay = Math.max(0, ms + 250);
    const timer = setTimeout(() => {
      void (async () => {
        await endActiveImpersonationSession('expired');
        clear();
        await logout('impersonation_expired');
        showInfo('Impersonación finalizada', 'La sesión de soporte expiró.');
      })();
    }, delay);
    return () => clearTimeout(timer);
  }, [context, clear]);

  const exitImpersonation = useCallback(async () => {
    if (exiting) return;
    setExiting(true);
    try {
      await endActiveImpersonationSession('manual');
      clear();
      await logout('impersonation_ended');
    } finally {
      setExiting(false);
    }
  }, [clear, exiting]);

  return {
    active: context != null,
    targetEmail: userEmail,
    expiresAtLabel: context ? formatExpiresAt(context.expiresAt) : '',
    exiting,
    exitImpersonation,
  };
}
